import { useEffect } from 'react';
import { fa, group, toman, tomanBare } from './fa';
import type { PaymentMethod } from './types';
import type { Totals } from './money';

/*
 * Taking the money.
 *
 * The design offers four tiles. Three of them are real payment methods this
 * system records; the fourth, split, is not a method but a way of composing
 * them — which is exactly why payments are rows on the invoice rather than a
 * column. A split sale is two rows, and cash-up sums the cash ones.
 *
 * The card flow is the one with teeth. Between "amount sent to the terminal"
 * and "the terminal answered" there is a window where nobody knows whether the
 * customer has been charged, and the only safe behaviour is to refuse to send
 * again. A till that lets an impatient cashier re-send during that window will
 * eventually double-charge someone, and the shop will find out from the
 * customer's bank rather than from us.
 */

export type CardState = 'idle' | 'waiting' | 'declined';

const METHODS: { key: PaymentMethod | 'split'; icon: string; title: string; hint: string }[] = [
  { key: 'cash', icon: '₮', title: 'نقد', hint: 'با محاسبه باقی‌مانده' },
  { key: 'card', icon: '▭', title: 'کارت‌خوان', hint: 'مبلغ را روی دستگاه بزنید' },
  { key: 'transfer', icon: '⇄', title: 'کارت به کارت', hint: 'با شماره پیگیری' },
  { key: 'split', icon: '◫', title: 'ترکیبی', hint: 'نقد + کارت' },
];

export function PaymentScreen({
  totals, invoiceLabel, method, onMethod, cash, onCash, onKey,
  cardState, cardOutcome, cardBlocked, onSendToTerminal, resolvePanel,
  onCardApproved, onCardDeclinedRetry, rrn, onRrn,
  splitCashRial, onSplitCash, onConfirm, onBack, submitting, charging, error, offline,
}: {
  totals: Totals;
  invoiceLabel: string;
  method: PaymentMethod | 'split';
  onMethod: (m: PaymentMethod | 'split') => void;
  cash: number;
  onCash: (v: number) => void;
  onKey: (k: string) => void;
  cardState: CardState;
  /** What the reader last said, or null when nobody has asked it yet. */
  cardOutcome: { outcome: string; message: string; needsDecision: boolean } | null;
  /** True when an unresolved payment blocks this basket entirely. */
  cardBlocked: boolean;
  /*
   * The control that lifts the lock, supplied by the till.
   *
   * Rendered here rather than only on another screen because this is where a
   * cashier is standing when it happens, and the instruction on this screen is
   * to fetch a سرشیفت. Telling someone to get a supervisor and then making the
   * supervisor go hunting through menus is how a blocked basket stays blocked.
   */
  resolvePanel?: React.ReactNode;
  onSendToTerminal: () => void;
  onCardApproved: () => void;
  onCardDeclinedRetry: () => void;
  rrn: string;
  onRrn: (v: string) => void;
  splitCashRial: number;
  onSplitCash: (v: number) => void;
  onConfirm: () => void;
  onBack: () => void;
  submitting: boolean;
  /*
   * True while the amount is at the reader and the customer is entering their
   * PIN. Distinct from `submitting`, which is the sale being recorded AFTER
   * the reader has answered — the window this covers is the dangerous one.
   */
  charging: boolean;
  error: string | null;
  offline: boolean;
}) {
  const changeRial = cash * 10 - totals.totalRial;
  const splitRemain = totals.totalRial - splitCashRial;

  /*
   * Card is deliberately not "ready" from this button.
   *
   * The charge is what records a card sale, and it is driven by «ارسال مبلغ به
   * دستگاه». Leaving the confirm button live would let a cashier record a card
   * payment that no terminal ever took — the invoice says paid, the bank never
   * saw it, and the shift is short by the amount with nothing to explain it.
   *
   * The one exception is a till with no connected reader, where the cashier
   * confirms the standalone device's result by hand.
   */
  const manualCard = cardOutcome?.outcome === 'unavailable';

  const ready =
    method === 'cash' ? cash * 10 >= totals.totalRial
    : method === 'card' ? manualCard && !cardBlocked
    : method === 'split' ? splitRemain >= 0
    : true;

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      // Enter must obey the same rule as the button: a keyboard shortcut that
      // bypasses the card gate is the same bug with a faster trigger.
      if (e.key === 'Enter' && ready && !submitting && !charging) onConfirm();
    };
    window.addEventListener('keydown', onEnter);
    return () => window.removeEventListener('keydown', onEnter);
  }, [ready, submitting, charging, onConfirm]);

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '16px 18px', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              width: 44, height: 44, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
              background: 'var(--luma-card)', fontSize: 16,
            }}
          >
            →
          </button>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
              پرداخت · {invoiceLabel}
            </div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>روش پرداخت را انتخاب کنید</div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 12, color: 'var(--luma-muted)', fontWeight: 600 }}>مبلغ قابل پرداخت</div>
            <div className="pos-num" style={{ fontWeight: 800, fontSize: 32, lineHeight: 1.1 }}>
              {toman(totals.totalRial)}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          {METHODS.map((m) => {
            const active = method === m.key;
            return (
              <button
                key={m.key}
                onClick={() => onMethod(m.key)}
                disabled={cardState === 'waiting' && m.key !== 'card'}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                  minHeight: 88, padding: '12px 14px', borderRadius: 12, textAlign: 'right',
                  background: 'var(--luma-card)',
                  border: `1px solid ${active ? 'var(--luma-accent)' : 'var(--luma-line)'}`,
                  boxShadow: active ? '0 0 0 4px rgba(249,124,21,.14)' : undefined,
                  opacity: cardState === 'waiting' && m.key !== 'card' ? 0.5 : 1,
                }}
              >
                <div style={{ fontSize: 20 }}>{m.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14.5 }}>{m.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--luma-muted)' }}>{m.hint}</div>
              </button>
            );
          })}
        </div>

        <div
          style={{
            flex: 1, minHeight: 0, background: 'var(--luma-card)', border: '1px solid var(--luma-line)',
            borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
          }}
        >
          {method === 'cash' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>مبلغ دریافتی از مشتری</div>
              <div
                style={{
                  height: 66, border: '1px solid var(--luma-line-strong)', borderRadius: 12,
                  background: 'var(--luma-sunken)', display: 'flex', alignItems: 'center',
                  padding: '0 16px', boxShadow: 'inset 0 0 0 3px rgba(249,124,21,.08)',
                }}
              >
                <div className="pos-num" style={{ flex: 1, fontWeight: 800, fontSize: 30 }}>{group(cash)}</div>
                <div style={{ fontSize: 13, color: 'var(--luma-muted)', fontWeight: 600 }}>تومان</div>
              </div>

              {/*
                Quick amounts are the notes a customer actually hands over.
                Rounded up to the nearest 50k and 100k because that is what is
                in a wallet — not arbitrary multiples of the total.
              */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { label: 'مبلغ دقیق', v: Math.ceil(totals.totalRial / 10) },
                  { label: null, v: Math.ceil(totals.totalRial / 10 / 50_000) * 50_000 },
                  { label: null, v: Math.ceil(totals.totalRial / 10 / 100_000) * 100_000 },
                  { label: 'پاک کردن', v: 0 },
                ].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => onCash(q.v)}
                    style={{
                      height: 48, padding: '0 16px', borderRadius: 10,
                      border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                      fontWeight: 700, fontSize: 14,
                    }}
                  >
                    {q.label ?? group(q.v)}
                  </button>
                ))}
              </div>

              <div
                style={{
                  borderRadius: 12, padding: 14,
                  border: `1px solid ${changeRial >= 0 ? 'var(--luma-tint-success-border)' : 'var(--luma-tint-warning-border)'}`,
                  background: changeRial >= 0 ? 'var(--luma-tint-success-bg)' : 'var(--luma-tint-warning-bg)',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)' }}>
                  {changeRial >= 0 ? 'باقی‌مانده برای مشتری' : 'کسری دریافت'}
                </div>
                <div className="pos-num" style={{ fontWeight: 800, fontSize: 30, marginTop: 2 }}>
                  {toman(Math.abs(changeRial))}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--luma-muted)', marginTop: 2 }}>
                  {changeRial >= 0
                    ? 'مبلغ دریافتی باید از مبلغ فاکتور کمتر نباشد.'
                    : 'می‌توانید بقیه را با کارت‌خوان دریافت کنید (پرداخت ترکیبی).'}
                </div>
              </div>
            </div>
          )}

          {method === 'card' && cardState !== 'declined' && (
            <div
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 14, textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 74, height: 74, borderRadius: 18,
                  background: cardBlocked ? 'var(--luma-tint-danger-bg)' : 'var(--luma-accent-tint)',
                  border: `1px solid ${cardBlocked ? 'var(--luma-tint-danger-border)' : 'var(--luma-tint-warning-border)'}`,
                  display: 'grid', placeItems: 'center', fontSize: 30,
                  color: cardBlocked ? 'var(--luma-tint-danger-text)' : 'var(--luma-accent-hover)',
                  animation: submitting || charging ? 'lumaPulse 1.6s ease-in-out infinite' : undefined,
                }}
              >
                {cardBlocked ? '!' : '▭'}
              </div>

              {/*
                The blocking state comes first, because nothing else on this
                screen is actionable while it holds. The customer may already
                have paid, and the till will not take the money again until a
                person has looked at the terminal's own receipt.
              */}
              {cardBlocked ? (
                <>
                  <div style={{ fontWeight: 800, fontSize: 20 }}>نتیجه پرداخت نامشخص است</div>
                  <div style={{ fontSize: 14, color: 'var(--pos-ink-3)', maxWidth: '48ch', lineHeight: 1.8 }}>
                    ممکن است مبلغ از مشتری کسر شده باشد. رسید دستگاه کارت‌خوان را ببینید و از سرشیفت
                    بخواهید وضعیت این فاکتور را مشخص کند. تا آن زمان پرداخت دوباره ممکن نیست.
                  </div>
                  <button
                    onClick={onBack}
                    style={{
                      height: 52, padding: '0 22px', borderRadius: 12,
                      border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                      fontWeight: 700, fontSize: 14.5,
                    }}
                  >
                    بازگشت به سبد
                  </button>
                  {resolvePanel ? (
                    <div style={{ width: '100%', maxWidth: 560, marginTop: 6 }}>{resolvePanel}</div>
                  ) : null}
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 800, fontSize: 20 }}>
                    {submitting || charging ? 'در انتظار پاسخ دستگاه…' : 'ارسال مبلغ به کارت‌خوان'}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--pos-ink-3)', maxWidth: '46ch', lineHeight: 1.8 }}>
                    {submitting
                      ? 'مشتری باید کارت را بکشد و رمز را وارد کند. تا پاسخ قطعی دستگاه صبر کنید — این صفحه را نبندید.'
                      : `مبلغ ${toman(totals.totalRial)} روی دستگاه نمایش داده می‌شود.`}
                  </div>

                  {/*
                    One button, and it is disabled while a charge is in flight.
                    A second press during the window where the customer is
                    entering their PIN is exactly how a shop takes the money
                    twice.
                  */}
                  <button
                    onClick={onSendToTerminal}
                    disabled={submitting}
                    style={{
                      height: 56, padding: '0 26px', borderRadius: 12,
                      background: submitting ? 'var(--pos-disabled)' : 'var(--luma-accent)',
                      color: '#FFF', fontWeight: 800, fontSize: 16,
                    }}
                  >
                    {submitting ? 'در حال ارسال…' : 'ارسال مبلغ به دستگاه'}
                  </button>

                  {/*
                    The manual path, for a till with no connected reader — a
                    standalone کارتخوان, which is most shops today. Only offered
                    once the agent has said it has no terminal, so it never
                    competes with the real one.
                  */}
                  {cardOutcome?.outcome === 'unavailable' && (
                    <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 13, color: 'var(--luma-accent-tint-text)' }}>
                        {cardOutcome.message}
                      </div>
                      <input
                        value={rrn}
                        onChange={(e) => onRrn(e.target.value)}
                        placeholder="شماره پیگیری از رسید کارتخوان"
                        className="pos-num"
                        style={{
                          height: 56, borderRadius: 12, textAlign: 'center',
                          border: '1px solid var(--luma-line-strong)', background: 'var(--luma-sunken)',
                          fontSize: 18, fontWeight: 700,
                        }}
                      />
                      <button
                        onClick={onCardApproved}
                        style={{
                          height: 52, borderRadius: 12, background: 'var(--luma-accent)',
                          color: '#FFF', fontWeight: 800, fontSize: 15,
                        }}
                      >
                        دستگاه تأیید کرد — ثبت فاکتور
                      </button>
                      <button
                        onClick={onCardDeclinedRetry}
                        style={{
                          height: 48, borderRadius: 12,
                          border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                          fontWeight: 700, fontSize: 14,
                        }}
                      >
                        دستگاه رد کرد
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {method === 'card' && cardState === 'declined' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{
                  background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
                  borderRadius: 12, padding: 16, display: 'flex', gap: 12,
                }}
              >
                <div
                  style={{
                    width: 40, height: 40, borderRadius: 10, background: 'var(--pos-danger-chip)',
                    display: 'grid', placeItems: 'center', color: 'var(--luma-tint-danger-text)',
                    fontSize: 18, fontWeight: 800,
                  }}
                >
                  ✕
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 17 }}>تراکنش کارت انجام نشد</div>
                  <div style={{ fontSize: 13.5, color: 'var(--pos-danger-body)', marginTop: 2 }}>
                    هیچ مبلغی از مشتری کسر نشده و فاکتور دست‌نخورده باقی مانده است.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => onMethod('card')}
                  style={{
                    height: 52, padding: '0 22px', borderRadius: 12, background: 'var(--luma-accent)',
                    color: '#FFF', fontWeight: 800, fontSize: 15,
                  }}
                >
                  تلاش دوباره با کارت
                </button>
                <button
                  onClick={() => onMethod('cash')}
                  style={{
                    height: 52, padding: '0 20px', borderRadius: 12,
                    border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                    fontWeight: 700, fontSize: 14.5,
                  }}
                >
                  پرداخت نقدی
                </button>
                <button
                  onClick={() => onMethod('split')}
                  style={{
                    height: 52, padding: '0 20px', borderRadius: 12,
                    border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                    fontWeight: 700, fontSize: 14.5,
                  }}
                >
                  پرداخت ترکیبی
                </button>
              </div>
              <div
                style={{
                  background: 'var(--luma-sunken)', border: '1px solid var(--pos-rule-strong)',
                  borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
                  color: 'var(--pos-ink-3)', lineHeight: 1.6,
                }}
              >
                محافظ برداشت دوتایی فعال است: تا وقتی نتیجه قطعی تراکنش مشخص نشده، لوما فاکتور را ثبت نمی‌کند
                و اجازه ارسال دوباره مبلغ را نمی‌دهد.
              </div>
            </div>
          )}

          {method === 'transfer' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>شماره پیگیری انتقال</div>
              <input
                value={rrn}
                onChange={(e) => onRrn(e.target.value)}
                placeholder="شماره پیگیری یا چهار رقم آخر کارت"
                className="pos-num"
                style={{
                  height: 60, borderRadius: 12, border: '1px solid var(--luma-line-strong)',
                  background: 'var(--luma-sunken)', padding: '0 16px', fontSize: 20, fontWeight: 700,
                }}
              />
              <div style={{ fontSize: 12.5, color: 'var(--pos-ink-3)', lineHeight: 1.65 }}>
                پیش از ثبت، رسید واریز را روی گوشی مشتری ببینید. مبلغ کامل فاکتور به‌عنوان انتقال ثبت می‌شود.
              </div>
            </div>
          )}

          {method === 'split' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>تقسیم مبلغ بین روش‌ها</div>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: '1px solid var(--pos-rule-strong)', borderRadius: 12,
                  padding: '12px 14px', background: 'var(--luma-sunken)',
                }}
              >
                <div style={{ flex: 1, fontWeight: 700, fontSize: 14.5 }}>نقد</div>
                <input
                  value={group(splitCashRial / 10)}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^\d۰-۹]/g, '');
                    onSplitCash(Number(digits.replace(/[۰-۹]/g, (c) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(c)))) * 10);
                  }}
                  className="pos-num"
                  style={{
                    width: 180, height: 44, borderRadius: 10, textAlign: 'left',
                    border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
                    fontWeight: 800, fontSize: 20, padding: '0 10px',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: '1px solid var(--pos-rule-strong)', borderRadius: 12,
                  padding: '12px 14px', background: 'var(--luma-sunken)',
                }}
              >
                <div style={{ flex: 1, fontWeight: 700, fontSize: 14.5 }}>کارت‌خوان</div>
                <div className="pos-num" style={{ fontWeight: 800, fontSize: 20 }}>
                  {toman(Math.max(0, splitRemain))}
                </div>
              </div>
              <div
                style={{
                  background: splitRemain < 0 ? 'var(--luma-tint-danger-bg)' : 'var(--luma-accent-tint)',
                  border: `1px solid ${splitRemain < 0 ? 'var(--luma-tint-danger-border)' : 'var(--luma-tint-warning-border)'}`,
                  borderRadius: 12, padding: '12px 14px', fontSize: 13,
                  color: splitRemain < 0 ? 'var(--luma-tint-danger-text)' : 'var(--luma-accent-tint-text)',
                  fontWeight: 600,
                }}
              >
                {splitRemain < 0
                  ? `سهم نقد از مبلغ فاکتور بیشتر است — ${toman(-splitRemain)} اضافه`
                  : `باقی‌مانده با کارت: ${toman(splitRemain)} — آماده ثبت`}
              </div>
            </div>
          )}

          <div style={{ flex: 1 }} />

          {error && (
            <div
              style={{
                background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
                borderRadius: 12, padding: '12px 14px', fontSize: 13.5,
                color: 'var(--luma-tint-danger-text)', fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <button
            onClick={onConfirm}
            disabled={!ready || submitting}
            style={{
              height: 60, borderRadius: 12,
              background: ready && !submitting ? 'var(--luma-accent)' : 'var(--pos-disabled)',
              color: '#FFF', fontWeight: 800, fontSize: 17,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 20px', boxShadow: '0 3px 10px rgba(249,124,21,.28)',
            }}
          >
            <span>
              {submitting
                ? 'در حال ثبت…'
                : method === 'cash' ? 'تأیید پرداخت نقدی و چاپ فاکتور'
                : method === 'card' ? 'ثبت پرداخت کارتی و چاپ فاکتور'
                // Only reachable on a till with no connected reader; the
                // connected path is driven by the terminal itself.
                : method === 'transfer' ? 'ثبت انتقال و چاپ فاکتور'
                : 'ثبت پرداخت ترکیبی و چاپ فاکتور'}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>Enter</span>
          </button>
        </div>
      </div>

      {/* --- numpad -------------------------------------------------------- */}
      <div
        style={{
          width: 392, flex: 'none', background: 'var(--luma-card)',
          borderInlineStart: '1px solid var(--luma-line)', padding: 16,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
          صفحه‌کلید عددی
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {['7', '8', '9', '4', '5', '6', '1', '2', '3', '000', '0', 'del'].map((k) => (
            <button
              key={k}
              onClick={() => onKey(k)}
              style={{
                height: 66, borderRadius: 12, border: '1px solid var(--luma-line)',
                background: 'var(--luma-sunken)', fontFamily: 'var(--luma-font-money)',
                fontWeight: 700, fontSize: 23,
              }}
            >
              {k === 'del' ? '⌫' : fa(k)}
            </button>
          ))}
        </div>

        <div
          style={{
            borderTop: '1px solid var(--pos-rule)', paddingTop: 12,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          <Line label={`${fa(totals.count)} قلم کالا`} value={toman(totals.subtotalRial)} />
          <Line label="مالیات ارزش افزوده" value={toman(totals.vatRial)} />
          <Line
            label="تخفیف"
            value={toman(totals.lineDiscountRial + totals.basketDiscountRial)}
            tone="var(--luma-accent-tint-text)"
          />
        </div>

        <div style={{ flex: 1 }} />

        {offline && (
          <div
            style={{
              background: 'var(--luma-tint-warning-bg)', border: '1px solid var(--luma-tint-warning-border)',
              borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
              color: 'var(--luma-accent-tint-text)', lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 2 }}>پرداخت آفلاین بدون مانع</div>
            نقد و کارت‌خوان مستقل کار می‌کنند. شماره فاکتور در صف می‌ماند و خودکار تکمیل می‌شود.
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--luma-ink-2)' }}>
      <span>{label}</span>
      <span className="pos-num" style={{ fontWeight: 600, color: tone ?? 'var(--luma-ink)' }}>{value}</span>
    </div>
  );
}

export { tomanBare };
